import type { AnswerValue, MedicationIntake, MedicationSetting, Question } from "@/domain/entities";
import type { ValidationCode } from "@/domain/scenario/ScenarioRunner";
import { useLocale } from "../../i18n/LocaleProvider";
import { MedicationInput } from "./MedicationInput";
import { MultiInput } from "./MultiInput";
import { NumberInput } from "./NumberInput";
import { ScaleInput } from "./ScaleInput";
import { SingleInput } from "./SingleInput";
import { TextInput } from "./TextInput";
import { TimeInput } from "./TimeInput";
import { YesNoInput } from "./YesNoInput";
import styles from "./questions.module.css";

export interface QuestionInputProps {
  question: Question;
  value: AnswerValue | undefined;
  medicines?: MedicationSetting[];
  error?: ValidationCode | null;
  /** `undefined` skips an optional question. */
  onSubmit: (value: AnswerValue | undefined) => void;
}

export function QuestionInput({ question, value, medicines = [], error = null, onSubmit }: QuestionInputProps) {
  const { t } = useLocale();
  const optional = question.required === false;
  const input = (() => {
    switch (question.type) {
      case "yesno":
        return <YesNoInput value={typeof value === "boolean" ? value : undefined} labels={question.labels} onSubmit={onSubmit} />;
      case "scale":
        return <ScaleInput question={question} value={typeof value === "number" ? value : undefined} onSubmit={onSubmit} />;
      case "single":
        return <SingleInput question={question} value={typeof value === "string" ? value : undefined} onSubmit={onSubmit} />;
      case "multi":
        return <MultiInput question={question} value={Array.isArray(value) ? (value as string[]) : undefined} optional={optional} onSubmit={onSubmit} />;
      case "time":
        return <TimeInput question={question} value={typeof value === "string" ? value : undefined} optional={optional} onSubmit={onSubmit} />;
      case "number":
        return <NumberInput question={question} value={typeof value === "number" ? value : undefined} optional={optional} onSubmit={onSubmit} />;
      case "text":
        return <TextInput value={typeof value === "string" ? value : undefined} optional={optional} onSubmit={onSubmit} />;
      case "medication":
        return (
          <MedicationInput
            value={Array.isArray(value) ? (value as MedicationIntake[]) : undefined}
            medicines={medicines}
            optional={optional}
            onSubmit={onSubmit}
          />
        );
    }
  })();

  return (
    <div className={styles.stack}>
      {input}
      {error && (
        <p role="alert" className={styles.error}>
          {t.questionnaire.invalid[error]}
        </p>
      )}
    </div>
  );
}
